import { useRothko } from "web/react/hooks/use-rothko/use-rothko";
import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import { trackKevelEvent } from "web/script/utils/ad-analytics";
import {
    CampaignRawBranchLink,
    getBranchCampaignName,
    RawBranchLink,
} from "web/script/utils/branch-io";
import { WebToAppCampaignSerializer } from "web/types/serializers";

type CampaignType = "top_banner" | "block_modal";
let fetchResolver;

export interface CampaignOptions {
    isIcon?: boolean;
    hasBeenBlocked?: boolean;
    hasBeenDismissed?: boolean;
    action?: string;
    contextType?: string;
    isAffiliate?: boolean;
    hasPromotion?: boolean;
    continueClicked?: boolean;
}

export interface CampaignDetails {
    branchLink: string;
    name: string;
    blockType?: string | null;
    titleKey?: string | null;
    componentType: string;
}

export interface WebToAppCampaign {
    isKevelEnabled: boolean;
    campaignDetails: CampaignDetails | undefined;
    onClick: () => void;
    onLoad: () => void;
    fetchCampaign: () => Promise<CampaignDetails | undefined>;
}

function getCampaignDetails(
    data: WebToAppCampaignSerializer,
    campaignType: CampaignType
): CampaignDetails | undefined {
    const validBranchLinks = campaignType === "top_banner" ? CampaignRawBranchLink : RawBranchLink;
    let branchLink = "";
    if (data.branch_link_name) {
        branchLink = validBranchLinks[data.branch_link_name];

        if (!branchLink) {
            console.error("Not a valid branch_link_name");
            return undefined;
        }
    }

    return {
        branchLink,
        titleKey: data.title_key,
        blockType: data.block_type,
        componentType: data.component_type,
        name: getBranchCampaignName(branchLink),
    };
}

export function isExperimentRunning(): boolean {
    if (environment.getFeature("mg_remove_leads_on_related_products_pdp")) {
        return true;
    }

    if (environment.getFeature("mg_continue_to_retailer_block_on_feeds")) {
        return true;
    }
    if (environment.getFeature("mg_chain_block_benefits_product_card")) {
        return true;
    }
    if (environment.getFeature("mg_second_block_sign_up_feeds")) {
        return true;
    }

    return false;
}

export default function useWebToAppCampaign(
    campaignType: CampaignType,
    options: CampaignOptions = {},
    enabled = true
): WebToAppCampaign {
    const endpoint = "modules/web_to_app_campaign";
    const searchParams = new URLSearchParams(Object.entries(options));
    const query = {
        campaignType: campaignType,
        feedType: environment.get("feedType"),
        pageType: environment.get("pageType"),
        pageSubType: environment.get("pageSubType"),
        initialPageType: analytics.getInitialPageType(),
        initialPageSubType: analytics.getInitialSubPageType(),
        ...searchParams.toJSON(),
    };
    const isKevelEnabled =
        Boolean(environment.getFeature("mg_kevel_campaigns")) && !isExperimentRunning();

    const { data, run, isFetched } = useRothko<WebToAppCampaignSerializer>(endpoint, query, {
        enabled: isKevelEnabled && enabled,
        rothko: { cache: true },
    });

    let campaignDetails;
    if (data) {
        campaignDetails = getCampaignDetails(data, campaignType);
    }

    function onClick(): void {
        if (data && data.click_url) {
            trackKevelEvent(data.click_url, "true");
        }
    }

    function onLoad(): void {
        if (data && data.impression_url) {
            trackKevelEvent(data.impression_url, "false");
        }
    }

    function fetchCampaign(): Promise<CampaignDetails | undefined> {
        return new Promise((resolve) => {
            if (isKevelEnabled) {
                run();
                fetchResolver = resolve;
            } else {
                resolve(undefined);
            }
        });
    }

    if (isFetched && fetchResolver) {
        fetchResolver(campaignDetails);
        fetchResolver = null;
    }

    return {
        isKevelEnabled,
        campaignDetails,
        onClick,
        onLoad,
        fetchCampaign,
    };
}
